// ─── Admin Roles ────────────────────────────────────────────────
export type AdminRole = 'SUPER_ADMIN' | 'CONTENT_ADMIN' | 'FINANCE_ADMIN' | 'SUPPORT';

export const ADMIN_ROLE_LABELS: Record<AdminRole, { es: string; en: string }> = {
  SUPER_ADMIN: { es: 'Super Administrador', en: 'Super Admin' },
  CONTENT_ADMIN: { es: 'Admin de Contenido', en: 'Content Admin' },
  FINANCE_ADMIN: { es: 'Admin de Finanzas', en: 'Finance Admin' },
  SUPPORT: { es: 'Soporte', en: 'Support' },
};

// ─── Permission Levels ──────────────────────────────────────────
export type PermissionLevel = 'FULL' | 'READ' | 'NONE';

// ─── Route keys matching sidebar nav items ──────────────────────
export type RouteKey =
  | 'dashboard'
  | 'packages'
  | 'users'
  | 'finance'
  | 'analytics'
  | 'notifications'
  | 'banners'
  | 'audit'
  | 'settings';

// ─── Permission matrix ──────────────────────────────────────────
// Rows = roles, Columns = route keys
// FULL = read + write | READ = read only | NONE = no access
export const PERMISSIONS: Record<AdminRole, Record<RouteKey, PermissionLevel>> = {
  SUPER_ADMIN: {
    dashboard: 'FULL',
    packages: 'FULL',
    users: 'FULL',
    finance: 'FULL',
    analytics: 'FULL',
    notifications: 'FULL',
    banners: 'FULL',
    audit: 'FULL',
    settings: 'FULL',
  },
  CONTENT_ADMIN: {
    dashboard: 'FULL',
    packages: 'FULL',
    users: 'READ',
    finance: 'NONE',
    analytics: 'FULL',
    notifications: 'FULL',
    banners: 'FULL',
    audit: 'READ',
    settings: 'NONE',
  },
  FINANCE_ADMIN: {
    dashboard: 'FULL',
    packages: 'READ',
    users: 'FULL',
    finance: 'FULL',
    analytics: 'FULL',
    notifications: 'NONE',
    banners: 'NONE',
    audit: 'READ',
    settings: 'NONE',
  },
  SUPPORT: {
    dashboard: 'FULL',
    packages: 'READ',
    users: 'FULL',
    finance: 'READ',
    analytics: 'READ',
    notifications: 'FULL',
    banners: 'NONE',
    audit: 'READ',
    settings: 'NONE',
  },
};

/**
 * Check if a role has at least a given permission level for a route.
 */
export function hasPermission(
  role: AdminRole,
  route: RouteKey,
  minLevel: PermissionLevel = 'READ',
): boolean {
  const level = PERMISSIONS[role]?.[route] ?? 'NONE';
  if (level === 'NONE') return false;
  if (minLevel === 'READ') return level === 'READ' || level === 'FULL';
  if (minLevel === 'FULL') return level === 'FULL';
  return false;
}

/**
 * Check if a role can access a route at all (READ or FULL).
 */
export function canAccess(role: AdminRole, route: RouteKey): boolean {
  return hasPermission(role, route, 'READ');
}

/**
 * Check if a role has write permission for a route.
 */
export function canWrite(role: AdminRole, route: RouteKey): boolean {
  return hasPermission(role, route, 'FULL');
}

/**
 * Get all accessible routes for a role.
 */
export function getAccessibleRoutes(role: AdminRole): RouteKey[] {
  return (Object.keys(PERMISSIONS[role]) as RouteKey[]).filter((key) => canAccess(role, key));
}

/**
 * Map route keys to URL paths.
 */
export const ROUTE_PATHS: Record<RouteKey, string> = {
  dashboard: '/',
  packages: '/packages',
  users: '/users',
  finance: '/finance',
  analytics: '/analytics',
  notifications: '/notifications',
  banners: '/banners',
  audit: '/audit',
  settings: '/settings',
};

/**
 * Get route key from URL pathname.
 */
export function getRouteKeyFromPath(pathname: string): RouteKey | null {
  if (pathname === '/') return 'dashboard';
  const segment = pathname.split('/')[1];
  if (segment && segment in ROUTE_PATHS) return segment as RouteKey;
  return null;
}
